import { Box, IconButton, Typography, Chip } from "@mui/material";
import CustomDataGrid from "src/@core/components/data-grid";
import { styled } from "@mui/material/styles";
import { Edit, Delete, Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import moment from "moment";

function TableCulturalModule({
  rows,
  totalCount,
  setCurrentPage,
  currentPage,
  setPageSize,
  pageSize,
  loading,
  toggleEdit,
  toggleStatus,
  toggleDelete,
  canEdit = true,
  canDelete = true,
}) {
  const navigate = useNavigate();
  const statusColors = {
    Active: "#66bb6a",
    Inactive: "#FFB400",
  };
  const CustomChip = styled(Chip)(({ label }) => ({
    backgroundColor: statusColors[label] || "#666",
    color: "#fff",
    width: "100px",
    textTransform: "capitalize",
  }));

  const rowsWithSrNo = rows.map((row, index) => ({
    ...row,
    srNo: (currentPage - 1) * pageSize + index + 1,
  }));

  // Check if any actions are available (View is always available, so check Edit or Delete)
  const hasAnyActions = true; // View button is always visible

  // Base columns
  const baseColumns = [
    { field: "srNo", headerName: "Sr No", minWidth: 100, sortable: false },
    {
      field: "title",
      headerName: "Title",
      minWidth: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography noWrap variant="body2" textTransform="capitalize">
          {row.title}
        </Typography>
      ),
    },
    {
      field: "short_description",
      headerName: "Short Description",
      minWidth: 250,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography 
          noWrap 
          variant="body2" 
          sx={{ 
            maxWidth: 250,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.short_description}
        </Typography>
      ),
    },
    {
      field: "region_name",
      headerName: "Region",
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {row.regionData?.name || "N/A"}
        </Typography>
      ),
    },
    {
      field: "ai_setting",
      headerName: "AI Settings",
      minWidth: 300,
      sortable: false,
      renderCell: ({ row }) => {
        const aiSettings = row.ai_setting || {};
        return (
          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
            Genre: {aiSettings.Genre || "N/A"} | 
            Scale: {aiSettings.Scale || "N/A"} | 
            Tempo: {aiSettings.Tempo || "N/A"}
          </Typography>
        );
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => <CustomChip
        label={row.is_active ? "Active" : "Inactive"}
        onClick={() => toggleStatus(row)}
      />,
    },
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {moment(row.created_at).format("MMM DD, YYYY hh:mm A")}
        </Typography>
      ),
    },
  ];

  // Actions column - only include if there are any actions available
  // Note: View button is always visible (read permission), but Edit/Delete are conditional
  const actionsColumn = {
    field: "Actions",
    headerName: "Actions",
    minWidth: 250,
    sortable: false,
    renderCell: ({ row }) => {
      const isDeleted = row.status === 2;
      const disabledStyle = { color: "#B0B0B0" };
      return (
        <Box display="flex" gap="10px">
          <IconButton
            size="small"
            color="primary"
            onClick={() => navigate(`/cultural-module/${row.id}`)}
            title="View Details"
          >
            <Visibility />
          </IconButton>
          {canEdit && (
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => toggleEdit(e, row)}
              disabled={isDeleted}
              title="Edit"
            >
              <Edit sx={isDeleted ? disabledStyle : {}} />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => toggleDelete(e, row)}
              disabled={isDeleted}
              title="Delete"
            >
              <Delete sx={isDeleted ? disabledStyle : {}} />
            </IconButton>
          )}
        </Box>
      );
    },
  };

  // Combine columns
  const columns = [...baseColumns, actionsColumn];

  return (
    <CustomDataGrid
      getRowId={(row) => row.id}
      loading={loading}
      rowCount={totalCount}
      rows={rowsWithSrNo}
      columns={columns}
      currentPage={currentPage}
      pageSize={pageSize}
      setCurrentPage={setCurrentPage}
      setPageSize={setPageSize}
    />
  );
}

export default TableCulturalModule;
